import { useEffect, useMemo, useState } from 'react';
import CategorySelect from './components/CategorySelect.jsx';
import SupplierList from './components/SupplierList.jsx';
import { getAllSuppliers, getCategories, getPageToken, getSelectedPartyId, parsePropertyItems } from './services/api.js';

export default function App() {
  const [token, setToken] = useState('');
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryCode, setSelectedCategoryCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError('');

        const pageToken = getPageToken();
        setToken(pageToken);
        setSelectedPartyId(getSelectedPartyId());

        const [supplierResults, categoryResults] = await Promise.all([
          getAllSuppliers(pageToken),
          getCategories(pageToken)
        ]);

        if (cancelled) {
          return;
        }

        setSuppliers(supplierResults);
        setCategories(
          parsePropertyItems(categoryResults)
            .filter((category) => category.Code || category.Description)
            .map((category) => ({
              Code: category.Code ?? '',
              Description: category.Description ?? category.Code ?? ''
            }))
        );
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setError(err instanceof Error ? err.message : 'Unable to load suppliers.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSuppliers = useMemo(() => {
    if (!selectedCategoryCode) {
      return suppliers;
    }

    return suppliers.filter((supplier) => {
      const categoryString = supplier.Categories ?? '';

      return categoryString
        .split(',')
        .map((category) => category.trim())
        .some((category) => category.includes(selectedCategoryCode));
    });
  }, [suppliers, selectedCategoryCode]);

  return (
    <section className="supplier-directory" data-selected-party-id={selectedPartyId ?? ''} data-token-loaded={Boolean(token)}>
      <CategorySelect
        categories={categories}
        selectedCategoryCode={selectedCategoryCode}
        onChange={setSelectedCategoryCode}
        disabled={loading || Boolean(error)}
      />

      {loading && <p className="supplier-message">Loading suppliers...</p>}

      {error && (
        <div className="supplier-error" role="alert">
          <strong>Supplier directory could not load.</strong>
          <br />
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <p className="supplier-count">
            Showing {filteredSuppliers.length} of {suppliers.length} suppliers
          </p>
          <SupplierList suppliers={filteredSuppliers} />
        </>
      )}
    </section>
  );
}
